import { isApiReaderEnabledForShop } from '../auth/selectors';
import HttpV2 from '../httpv2';
import Http from '../http';
import webTerminal from './Terminal';
import apiTerminal from './ApiTerminal';
import {
  STRIPE_CONNECT_TOKEN,
  COLLECT_PAYMENT_METHOD_SUCCESS,
  PROCESS_PAYMENT_SUCCESS,
  READER_TRANSACTION_SUCCESS,
  READER_TRANSACTION_FAIL,
  CANCEL_TRANSACTION,
  WEBREADER_ERROR,
  CAPTURE_REFUND,
  READER_REFUND_FAIL,
  READER_REFUND_SUCCESS,
} from './constants';
import { noAvailableReaders } from '../register/webReader/actions';
import * as paymentActions from '../register/payment/actions';
import { isReaderOnline } from '../register/webReader/selector';
import { getStrategyByName } from './helpers';
import { Logger } from '../../modules/infra/observability/logger';

export const saleChargeSuccess = (payload, strategy) => ({ type: READER_TRANSACTION_SUCCESS, payload, strategy });
export const saleChargeFail = (payload, strategy) => ({ type: READER_TRANSACTION_FAIL, payload, strategy });

export const refundSuccess = (payload, strategy) => ({ type: READER_REFUND_SUCCESS, payload, strategy });
export const refundFail = (payload, strategy) => ({ type: READER_REFUND_FAIL, payload, strategy });

const getTerminal = (strategyName, isApiReader) => {
  const canUseApiTerminal = isApiReader && strategyName === 'pay';

  return canUseApiTerminal ? apiTerminal : webTerminal;
};

const collectPaymentMethodSuccess = (payload, strategy, isApiReader) => ({
  type: COLLECT_PAYMENT_METHOD_SUCCESS,
  payload,
  strategy,
  isApiReader,
});

const processPaymentSuccess = (payload, strategy) => ({
  type: PROCESS_PAYMENT_SUCCESS,
  payload,
  strategy,
});

export const webreaderError = (data, strategy) => ({
  type: WEBREADER_ERROR,
  payload: data,
  strategy,
});

export const captureRefund = () => ({ type: CAPTURE_REFUND });

export const discoverReaders = async (readerLocationId, dispatch, strategy, isApiReader) => {
  const terminal = getTerminal(strategy, isApiReader);
  const result = await terminal.discoverReaders(readerLocationId);
  const strategyHandlers = getStrategyByName(strategy);
  strategyHandlers.setIsApiReader(isApiReader);

  Logger.log('Discover readers result', {
    strategy,
    error: result.error,
    location: readerLocationId,
    count: result.discoveredReaders ? result.discoveredReaders.length : null,
    action: 'Discover readers result',
    terminalType: terminal.getName(),
    readers: result.discoveredReaders,
  });

  if (result.error) {
    dispatch(webreaderError({ error: result.error }, strategy));
    strategyHandlers.discoverFail(result.error, dispatch);
    return;
  }

  const readers = result.discoveredReaders;

  if (!readers.length) {
    dispatch(noAvailableReaders());
    strategyHandlers.discoverFail(result.error, dispatch);
    return;
  }

  const readersOnline = readers.filter(isReaderOnline);

  if (readersOnline.length === 1) {
    strategyHandlers.selectReaderDone(readersOnline, dispatch);
    return;
  }

  strategyHandlers.selectReader(readersOnline, dispatch);
};

export const handleReaderSelect = dispatch => async (state, action) => {
  const terminal = getTerminal(action.strategy, isApiReaderEnabledForShop(state));
  const result = await terminal.connect(action.payload);

  Logger.log('Connect result', {
    error: result.error,
    strategy: action.strategy,
    location: terminal.readerLocationId,
    terminalType: terminal.getName(),
    action: 'Connect result',
  });

  const strategyHandlers = getStrategyByName(action.strategy);

  if (result.error) {
    strategyHandlers.readerConnectError(result.error, dispatch);
    return;
  }

  strategyHandlers.readerConnectSuccess(dispatch);
};

export const handleReaderSelectCancel = dispatch => (state, action) => {
  const strategyHandlers = getStrategyByName(action.strategy);
  strategyHandlers.readerSelectCancel(dispatch);
};

export const stripeConnectTokenAction = strategy => ({
  type: STRIPE_CONNECT_TOKEN,
  strategy,
});

const trackWebReaderModel = state => (paymentIntent, isApiReader) => {
  try {
    if (!isApiReader && paymentIntent && paymentIntent.id) {
      const reader = state.register.webReader.selectedReader;

      if (reader) {
        Http.post(`/pos/track_terminal/${paymentIntent.id}`, { terminal: reader.device_type })
          .then((result) => {
            if (result.status !== 'ok' && result.message) {
              Logger.error('Failed to track web reader model', {
                error: result.message,
                action: 'Failed to track web reader model',
              });
            }
          })
          .catch((error) => {
            Logger.error('Failed to track web reader model', {
              error,
              action: 'Failed to track web reader model',
            });
          });
      }
    }
  } catch (e) {
    // do nothing
  }
};

export const collectPaymentMethod = (dispatch, state) => async (payment, strategy, isApiReader, configOverrides) => {
  const requiredDetails = isApiReader ? payment.paymentIntentId : payment.paymentIntentSecret;
  const terminal = getTerminal(strategy, isApiReader);
  const result = await terminal.collectPayment(requiredDetails, configOverrides);

  Logger.log('Collect payment result', {
    strategy,
    error: result.error,
    location: terminal.readerLocationId,
    terminalType: terminal.getName(),
    action: 'Collect payment result',
  });

  if (result.error) {
    dispatch(webreaderError({ error: result.error }, strategy));
    dispatch(saleChargeFail(result.error, strategy));
    return;
  }

  trackWebReaderModel(state)(result.paymentIntent, isApiReader);
  dispatch(collectPaymentMethodSuccess(result.paymentIntent, strategy, isApiReader));
};

export const collectRefundPaymentMethod = dispatch => async (payload, strategy, isApiReader) => {
  const terminal = getTerminal(strategy, isApiReader);
  const result = await terminal.collectRefundPayment(...payload);

  Logger.log('Collect refund payment result', {
    strategy,
    error: result.error,
    location: terminal.readerLocationId,
    terminalType: terminal.getName(),
    action: 'Collect refund payment result',
  });

  if (result.error) {
    dispatch(webreaderError({ error: result.error }, strategy));
    dispatch(refundFail(result.error, strategy));
    return;
  }

  dispatch(collectPaymentMethodSuccess({}, strategy));
};

export const processPayment = dispatch => async (paymentIntent, strategy, isApiReader, configOverrides) => {
  const terminal = getTerminal(strategy, isApiReader);
  const result = await terminal.processPayment(paymentIntent, configOverrides);

  if (!result) {
    Logger.log('Process payment returned null', {
      strategy,
      error: null,
      location: terminal.readerLocationId,
      terminalType: terminal.getName(),
      action: 'Process payment returned null',
    });

    return;
  }

  Logger.log('Process payment result', {
    strategy,
    error: result.error,
    location: terminal.readerLocationId,
    terminalType: terminal.getName(),
    action: 'Process payment result',
  });

  if (result.error) {
    dispatch(webreaderError({ error: result.error }, strategy));
    dispatch(saleChargeFail(result.error, strategy));
    return;
  }

  dispatch(processPaymentSuccess(result.paymentIntent, strategy));
};

export const processRefund = dispatch => async (strategy, isApiReader) => {
  const terminal = getTerminal(strategy, isApiReader);
  const result = await terminal.processRefund();

  Logger.log('Process refund payment result', {
    strategy,
    error: result.error,
    location: terminal.readerLocationId,
    terminalType: terminal.getName(),
    action: 'Process refund payment result',
  });

  if (result.error) {
    dispatch(webreaderError({ error: result.error }, strategy));
    dispatch(refundFail({}, strategy));
    return;
  }

  dispatch(processPaymentSuccess(result.refund, strategy));
};

export const capturePayment = dispatch => (paymentIntent, strategy, isApiReader) => {
  const terminal = getTerminal(strategy, isApiReader);

  HttpV2.post(`/pos/capture/${paymentIntent.id}`).then((res) => {
    dispatch(saleChargeSuccess(res, strategy));
    Logger.log('Capture success', {
      strategy,
      location: terminal.readerLocationId,
      terminalType: terminal.getName(),
      action: 'Capture success',
    });
  }).catch((err) => {
    Logger.log('Capture error', {
      strategy,
      error: err,
      location: terminal.readerLocationId,
      terminalType: terminal.getName(),
      action: 'Capture error',
    });
    dispatch(saleChargeFail(err));
    dispatch(webreaderError({ error: { message: 'Process failed. Try again, please.' } }, 'pay'));
  });
};

export const cancelWebreaderTransaction = strategy => (dispatch) => {
  dispatch({ type: CANCEL_TRANSACTION, strategy });
};

export const resetWebreaderPayment = (state, action, dispatch) => {
  const terminal = getTerminal(action.strategy, isApiReaderEnabledForShop(state));

  dispatch(paymentActions.readerTransactionFail());
  terminal.dispose();
};
